import styles from "./App.module.scss";
import Footer from "../layout/Footer/Footer";
import getHeadEmbed from "./hooks/getHeadEmbed";
import Header from "../layout/Header/Header";
import setBodyStyles from "./hooks/setBodyStyles";
import Toolbar from "../layout/Toolbar/Toolbar";
import UmbracoPage from "./umbraco/pages/UmbracoPage";
import useAppLoading, { AppLoadingState } from "./hooks/useAppLoading";
import { thunkGetSiteSettings } from "./umbraco/redux/settings.actions";

import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import { Helmet } from "react-helmet-async";
import { Route } from "react-router-dom";
import { thunkLoadDictionary } from "components/Dictionary/redux/dictionary.actions";
import { useDispatch, useSelector } from "react-redux";
import AgeGuard from "components/AgeGuard/AgeGuard";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import isBrowserContext from "helpers/isBrowserContext";
import React, { Fragment, FunctionComponent, useEffect } from "react";
import useRekomCookieFormVisibilityController from "./hooks/useRekomCookieFormVisibilityController";
import Button from "components/Button/Button";
import RekomLoadingPage from "pages/RekomLoadingPage/RekomLoadingPage";
import Zendesk from "react-zendesk";

const App: FunctionComponent = () => {
    const dispatch = useDispatch();

    const settings = useSelector((state) => state.settings.content);
    const viewState = useAppLoading(settings);
    const bodyStyles = setBodyStyles(settings);
    useRekomCookieFormVisibilityController(viewState);

    const { headingFile, paragraphFile, faviconFile } = getHeadEmbed(settings);
    const headerButtonContentType =
        settings?.headerButtons?.[0] && settings.headerButtons.length > 0 ? settings.headerButtons[0].content._contentType : undefined;

    useEffect(() => {
        if (isBrowserContext()) {
            dispatch(thunkGetSiteSettings(`${window.location.origin}${window.location.pathname}`));
            dispatch(thunkLoadDictionary(`${window.location.origin}${window.location.pathname}`));
        }
    }, []);

    let headerType = "blockHeader1";
    if (settings?.theme?.header && settings?.theme?.header.length > 0) {
        headerType = settings?.theme?.header?.[0]?.content["_contentType"];
    }

    let footerType = "blockFooter1";
    if (settings?.theme?.footer && settings?.theme?.footer.length > 0) {
        footerType = settings?.theme?.footer?.[0]?.content["_contentType"];
    }

    const gtmParams = settings ? { id: settings?.tagManagerId || "GTM-KVWBQLN" } : undefined;

    return (
        <Fragment>
            <GTMProvider state={gtmParams}>
                {/* Zendesk */}
                {settings?.zenDeskID ? (
                    <Zendesk
                        defer
                        zendeskKey={settings.zenDeskID}
                    />
                ) : null}

                <div
                    className={styles.wrapper}
                    id="wrapper"
                    style={bodyStyles}
                >
                    {/* Background Media */}
                    {settings?.theme?.backgroundMedia?.[0] && settings.theme.backgroundMedia.length > 0 ? (
                        <BackgroundMedia
                            className={styles.background}
                            key={settings.theme.backgroundMedia.contentUdi}
                            {...settings.theme.backgroundMedia[0]}
                        />
                    ) : null}
                    <div className={styles.inner}>
                        <AgeGuard>
                            {!isBrowserContext() || viewState === AppLoadingState.LOADED ? (
                                <Header
                                    contentType={headerType}
                                    settings={settings}
                                />
                            ) : null}

                            <main className={styles.content}>
                                <Route path="*">
                                    <UmbracoPage />
                                </Route>
                            </main>

                            <RekomLoadingPage viewState={viewState} />

                            {!isBrowserContext() || viewState === AppLoadingState.LOADED ? (
                                <Footer
                                    contentType={footerType}
                                    settings={settings}
                                />
                            ) : null}

                            {!isBrowserContext() || viewState === AppLoadingState.LOADED ? <Toolbar settings={settings} /> : null}

                            {/* Buttons */}
                            {(settings && settings?.headerButtons && !isBrowserContext()) || viewState === AppLoadingState.LOADED ? (
                                <Fragment>
                                    {settings?.showActionsButtons ? (
                                        <div className={styles.action_buttons}>
                                            {(settings && settings?.headerButtons && !isBrowserContext()) || viewState === AppLoadingState.LOADED ? (
                                                <div className={styles.action_buttons}>
                                                    {settings &&
                                                        settings.headerButtons?.map((button: any, index: number) => {
                                                            if (button?.content._contentType === "fixrButton" || settings?.showActionsButtons) {
                                                                return (
                                                                    <Button
                                                                        key={index}
                                                                        to={button.content.link}
                                                                        className={styles.action_button}
                                                                        {...button.content}
                                                                    >
                                                                        {button.content.label}
                                                                    </Button>
                                                                );
                                                            } else {
                                                                return null;
                                                            }
                                                        })}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </Fragment>
                            ) : null}
                        </AgeGuard>
                    </div>
                </div>
            </GTMProvider>
            <Helmet>
                <link
                    rel="stylesheet"
                    href={headingFile}
                />
                <link
                    rel="stylesheet"
                    href={paragraphFile}
                />

                <link
                    rel="shortcut icon"
                    href={faviconFile}
                    type="image/png"
                />
            </Helmet>
        </Fragment>
    );
};

export default App;
